/* eslint-disable no-restricted-globals */
/* eslint-disable quote-props */

import { LevelEditorInstructions } from './LevelEditorInstructions';
import { LevelEditorGoalPanel } from './LevelEditorGoalPanel';
import { LevelEditorDropPanel } from './LevelEditorDropPanel';
import { EditorStorageManager } from '../MapEditor/EditorStorageManager';

const KEY_LABEL_ROWS = [
  {
    '1': 'candy_1',
    '2': 'candy_2',
    '3': 'candy_3',
    '4': 'candy_4',
    '5': 'candy_5',
    '6': 'candy_6',
    '7': 'candy_r',
    '8': 'candy_chest',
    '9': 'candy_goalCandy',
    'B': 'candy_spiral',
    'L': 'cake_5',
    'I': 'candy_burntCandy',
  },
  {
    'E': 'concrete_3',
    'R': 'dirt_2',
    'T': 'eraser',
    'Y': 'tile_1',
    'W': 'ice_front',
    'A': 'status_chain_2',
    'S': 'candy_infection',
    'C': 'collect_cell',
    'F': 'dirt_s_2',
    'G': 'jam_2',
    '0': 'candy_r',
    'P': 'infection_front',
    'N': 'gem_box_r',
  },
];

/**
 * Level Editor Side Panel Class
 */
export class LevelEditorSidePanel extends Phaser.Group {
  /**
   * constructor
   * @param {number} x
   * @param {Object} lvlData
   */
  constructor(x, lvlData) {
    super(game);
    this.x = x; // position side panel

    this.signals = {
      undo: new Phaser.Signal(),
      redo: new Phaser.Signal(),
      reset: new Phaser.Signal(),
      onFieldChanged: new Phaser.Signal(),
    };

    this._storageManager = EditorStorageManager.getInstance();
    this._state = game.state.getCurrentState();
    this._lvlData = lvlData;

    // make editor controls / layout
    this._initGoalPanel(0, 520);
    this._initDropConfigPanel(320, 520);
    const panelsBottomY = Math.max(this._goalPanel.bottom, this._dropPanel.bottom);
    this._makeKeyShortcutLabels(0, 0);
    this._makeBoardSizeControls(0, 150);
    this._makeMoveControls(0, 220);
    this._makeMaxNumberControls(0, 270);
    this._makeStarsReqControls(0, 320);
    this._makeTutorialIdControls(0, 370);
    this._makeGoalCandyLimitControls(0, 420);
    this._makePreboosterToggleControls(0, 740);
    this._makeShowAsHardControls(0, panelsBottomY);
    this._makeInstructionBtnControls(20, 1340);
    this._makeBackBtnControls(0, 1360);
    this._makeNavigationAndExportControls(0, 1420);
    this._makeUndoAndRedoControls(560, 1360);
    this._makeResetLevelControls(560, 1300);
    this._makeSaveStatusDisplay(560, 1240);
  }

  /**
   * init the goal selection panel
   * @param {number} x
   * @param {number} y
   */
  _initGoalPanel(x, y) {
    this._goalPanel = new LevelEditorGoalPanel(x, y, this._lvlData);
    this._goalPanel.signals.onGoalChanged.add(() => {
      this.signals.onFieldChanged.dispatch();
    });
    this.add(this._goalPanel);
  }

  /**
   * init drop config panel
   * @param {number} x
   * @param {number} y
   */
  _initDropConfigPanel(x, y) {
    this._dropPanel = new LevelEditorDropPanel(x, y, this._lvlData);
    this._dropPanel.signals.onDropFieldModified.add(() => {
      this.signals.onFieldChanged.dispatch();
    });
    this.add(this._dropPanel);
  }

  /**
   * make key shortcut labels
   * @param {number} x
   * @param {number} y
   */
  _makeKeyShortcutLabels(x, y) {
    const colSpacing = 70; const rowSpacing = 70;
    KEY_LABEL_ROWS.forEach((row, rowIndex) => {
      Object.keys(row).forEach((key, colIndex) => {
        this._makeKeyPreview(x + (colIndex * colSpacing), y + (rowIndex * rowSpacing), key, row[key]);
      });
    });
  }

  /**
   * make a text field at the specificied position
   * @param {number} x
   * @param {number} y
   * @param {string} text
   * @param {string} style
   */
  _makeTextField(x, y, text, style = 'editor') {
    const textField = new G.Text(x, y, text, style);
    this.add(textField);
    return textField;
  }

  /**
   * make a button with a text label
   * @param {number} x
   * @param {number} y
   * @param {string} text
   * @param {Function} clickFunc (optional)
   * @param {string} style (optional)
   */
  _makeTextFieldBtn(x, y, text, clickFunc = null, style = 'editor-aquamarine') {
    const textField = this._makeTextField(x, y, text, style);
    textField.inputEnabled = true;
    textField.input.useHandCursor = true;
    if (clickFunc) textField.events.onInputDown.add(clickFunc);
    return textField;
  }

  /**
   * make key preview image / text
   * @param {number} x
   * @param {number} y
   * @param {string} letter
   * @param {string} image
   */
  _makeKeyPreview(x, y, letter, image) {
    const group = game.add.group();
    group.position.setTo(x, y);
    const img = G.makeImage(0, 0, image, 0, group);
    img.width = G.l(60); img.height = G.l(60);
    group.add(this._makeTextField(0, 30, letter));
    this.add(group);
  }

  /**
   * make controls for changing the board size
   * @param {number} x
   * @param {number} y
   */
  _makeBoardSizeControls(x, y) {
    const state = game.state.getCurrentState();
    // create board size label
    this._makeTextField(x, y, 'Board size:');
    // create board width / height labels
    const widthText = this._makeTextField(x + 340, y, state.board.boardData.width.toString());
    const heightText = this._makeTextField(x + 460, y, state.board.boardData.height.toString());

    // create - width button
    const widthMinus = new G.Button(x + 320, y + 30, 'minus_ico', () => {
      state.changeBoardSize(Math.max(4, state.board.boardData.width - 1), Math.max(4, state.board.boardData.height));
      widthText.setText(state.board.boardData.width);
      this.signals.onFieldChanged.dispatch();
    });
    widthMinus.IMMEDIATE = true;

    // create + width button
    const widthPlus = new G.Button(x + 390, y + 30, 'plus_ico', () => {
      state.changeBoardSize(Math.max(4, state.board.boardData.width + 1), Math.max(4, state.board.boardData.height));
      widthText.setText(state.board.boardData.width);
      this.signals.onFieldChanged.dispatch();
    });
    widthPlus.IMMEDIATE = true;

    // create - height button
    const heightMinus = new G.Button(x + 440, y + 30, 'minus_ico', () => {
      state.changeBoardSize(Math.max(4, state.board.boardData.width), Math.max(4, state.board.boardData.height - 1));
      heightText.setText(state.board.boardData.height);
      this.signals.onFieldChanged.dispatch();
    });
    heightMinus.IMMEDIATE = true;

    // create + height button
    const heightPlus = new G.Button(x + 510, y + 30, 'plus_ico', () => {
      state.changeBoardSize(Math.max(4, state.board.boardData.width), Math.max(4, state.board.boardData.height + 1));
      heightText.setText(state.board.boardData.height);
      this.signals.onFieldChanged.dispatch();
    });
    heightPlus.IMMEDIATE = true;

    // add buttons to display
    this.addMultiple([widthMinus, widthPlus, heightPlus, heightMinus]);
  }

  /**
   * make controls for changing move count
   * @param {number} x
   * @param {number} y
   */
  _makeMoveControls(x, y) {
    const moveControlsGroup = this.add(game.make.group());
    moveControlsGroup.x = G.l(x); moveControlsGroup.y = G.l(y);
    const moveLabel = this._makeTextField(0, 0, 'Moves:');
    const movesNr = this._makeTextFieldBtn(200, 0, this._lvlData.moves.toString());
    movesNr.events.onInputDown.add(() => {
      G.Utils.debugPrompt('Enter moves number').then(((answer) => {
        if (isNaN(parseInt(answer))) return;
        this._lvlData.moves = parseInt(answer);
        movesNr.setText(parseInt(answer).toString());
        this.signals.onFieldChanged.dispatch();
      }));
    });
    moveControlsGroup.addMultiple([moveLabel, movesNr]);
  }

  /**
   * make controls to set max candy types for a level
   * @param {number} x
   * @param {number} y
   */
  _makeMaxNumberControls(x, y) {
    const maxNrGroup = new Phaser.Group(game);
    this.addChild(maxNrGroup);
    maxNrGroup.x = G.l(x); maxNrGroup.y = G.l(y);
    const maxNrLabel = this._makeTextField(0, 0, 'Types of candies:');
    maxNrGroup.add(maxNrLabel);

    // create candy icons
    const icons = [];
    for (let i = 1; i <= 6; i++) {
      const img = G.makeImage(400 + i * 50, 0, `candy_${i}`, 0);
      img.width = img.height = 50;
      icons.push(img);
      maxNrGroup.addChild(img);
    }
    // referesh candy icons
    const refreshIcons = (nrOfTypes) => { icons.forEach((img, i) => { img.tint = i < nrOfTypes ? 0xffffff : 0x333333; }); };
    refreshIcons(this._lvlData.nrOfTypes);

    // create button / prompt to edit max candy count
    const maxNrBtn = this._makeTextFieldBtn(400, 0, this._lvlData.nrOfTypes.toString());
    maxNrBtn.events.onInputDown.add(() => {
      G.Utils.debugPrompt('Enter max candy number (4-6)').then(((answer) => {
        const parsedAnswer = parseInt(answer);
        if (isNaN(parsedAnswer)) return;
        this._lvlData.nrOfTypes = parseInt(answer);
        maxNrBtn.setText(parseInt(answer).toString());
        refreshIcons(this._lvlData.nrOfTypes);
        this.signals.onFieldChanged.dispatch();
      }));
    });
    maxNrGroup.add(maxNrBtn);
  }

  /**
   * make controls to set star requirement for the level
   * @param {number} x
   * @param {number} y
   */
  _makeStarsReqControls(x, y) {
    const starsReqGroup = this.add(game.make.group());
    starsReqGroup.position.setTo(G.l(x), G.l(y));
    starsReqGroup.add(this._makeTextField(0, 0, 'Stars:'));
    for (let i = 0; i < 3; i++) {
      const btn = this._makeTextFieldBtn(200 + (i * 200), 0, this._lvlData.starsReq[i].toString());
      btn.index = i;
      btn.events.onInputDown.add(() => {
        G.Utils.debugPrompt(`Enter requirement for ${btn.index + 1} stars:`).then(((answer) => {
          const parsedAnswer = parseInt(answer);
          if (isNaN(parsedAnswer)) return;
          this._lvlData.starsReq[btn.index] = parsedAnswer;
          btn.setText(parsedAnswer.toString());
          this.signals.onFieldChanged.dispatch();
        }));
      });
      starsReqGroup.add(btn);
    }
  }

  /**
   * make controls for setting the tuturial id
   * @param {number} x
   * @param {number} y
   */
  _makeTutorialIdControls(x, y) {
    const group = this.add(game.add.group());
    group.position.setTo(x, y);
    group.label = group.add(this._makeTextField(0, 0, 'Tutorial ID:'));
    const { tutID } = this._lvlData;
    group.switch = this._makeTextFieldBtn(300, 0, tutID || '---', () => {
      G.Utils.debugPrompt('Enter tutorial ID').then((id) => {
        if (id.length === 0) {
          delete this._lvlData.tutID;
          group.switch.setText('---');
          group.switch.fill = '#a8dbc6';
        } else {
          this._lvlData.tutID = id;
          group.switch.setText(id);
          group.switch.fill = G.json.tutorials[id] ? 'green' : 'orange';
        }
        this.signals.onFieldChanged.dispatch();
      });
    });
    group.add(group.switch);
    if (tutID && G.json.tutorials[tutID]) group.switch.fill = 'green';
  }

  /**
   * make controls for setting goal candy limit and toggling the goal cap
   * @param {number} x
   * @param {number} y
   */
  _makeGoalCandyLimitControls(x, y) {
    const group = this.add(game.add.group());
    group.position.setTo(x, y);
    // candy board limit button / text
    group.label = group.add(this._makeTextField(0, 0, 'Goal Candy board limit:'));
    const boardLimit = this._lvlData.goalCandyBoardLimit;
    group.switch = this._makeTextFieldBtn(550, 0, boardLimit || '---', () => {
      G.Utils.debugPrompt('Enter limit').then(((amount) => {
        const parsedAnswer = parseInt(amount);
        if (Number.isNaN(parsedAnswer)) {
          delete this._lvlData.goalCandyBoardLimit;
          group.switch.setText('---');
        } else {
          this._lvlData.goalCandyBoardLimit = parsedAnswer;
          group.switch.setText(parsedAnswer);
        }
        this.signals.onFieldChanged.dispatch();
      }));
    });
    group.add(group.switch);

    // goal cap button / text
    const initNoLimitBtnText = this._lvlData.goalCandyNoLimit ? 'No Goal Cap ✔️' : 'No Goal Cap ❌';
    group.noLimitBtn = this._makeTextFieldBtn(720, 0, initNoLimitBtnText, (() => {
      this._lvlData.goalCandyNoLimit = !this._lvlData.goalCandyNoLimit;
      group.noLimitBtn.text = this._lvlData.goalCandyNoLimit ? 'No Goal Cap ✔️' : 'No Goal Cap ❌';
      this.signals.onFieldChanged.dispatch();
    }));
    group.noLimitBtn.fill = 'white';
    group.add(group.noLimitBtn);
  }

  /**
   * make controls to toggle preboosters on / off
   * @param {number} x
   * @param {number} y
   */
  _makePreboosterToggleControls(x, y) {
    const preBoostersText = this._makeTextFieldBtn(x, y, this._lvlData.noPreBoosters ? 'Pre boosters ❌' : 'Pre boosters ✔️', null, 'editor');
    preBoostersText.events.onInputDown.add(() => {
      this._lvlData.noPreBoosters = !this._lvlData.noPreBoosters;
      preBoostersText.setText(this._lvlData.noPreBoosters ? 'Pre boosters ❌' : 'Pre boosters ✔️');
      this.signals.onFieldChanged.dispatch();
    });
    this.add(preBoostersText);
  }

  /**
   * make controls for settings as hard / normal levels
   * @param {number} x
   * @param {number} y
   */
  _makeShowAsHardControls(x, y) {
    const group = game.make.group(); group.left = x; group.top = y;
    this.add(group);
    // create show as type label
    const showAsTxt = this._makeTextField(0, 0, 'Show as:');
    group.add(showAsTxt);
    // make clickable icon
    const icon = G.makeImage(showAsTxt.right + 10, 0, null, 0, group);
    icon.inputEnabled = true; icon.input.useHandCursor = true;
    // update icon graphic state
    icon.updateIcon = () => { G.changeTexture(icon, this._lvlData.showAsHard ? 'map_point_hard' : 'map_point'); };
    // toggle hard level button
    icon.toggleHardLevel = () => {
      if (this._lvlData.showAsHard) delete this._lvlData.showAsHard;
      else this._lvlData.showAsHard = true;
    };
    // icon click event
    icon.events.onInputDown.add(() => {
      icon.toggleHardLevel(); icon.updateIcon();
      this.signals.onFieldChanged.dispatch();
    });
    icon.updateIcon();
  }

  /**
   * make a button to show the instructions overlay
   * @param {number} x
   * @param {number} y
   */
  _makeInstructionBtnControls(x, y) {
    const button = new G.Button(x, y, null);
    const gray = 0x808080;
    const iShape = new G.Text(0, 0, 'i', { style: 'font-white', fill: gray }, 0.5);
    const circle = new Phaser.Graphics(game);
    circle.lineStyle(4, gray); circle.drawCircle(-1, -5, iShape.height, iShape.height);
    button.addChild(circle); button.addChild(iShape);
    button.onClick.add(() => { game.world.addChild(new LevelEditorInstructions()); });
    this.add(button);
  }

  /**
   * make the back button
   * @param {number} x
   * @param {number} y
   */
  _makeBackBtnControls(x, y) {
    const button = this._makeTextFieldBtn(x, y, 'Back to WORLD MAP', () => {
      const { levelDataMgr } = G.Helpers;
      levelDataMgr.saveLevel(this._state.lvlId, this._exportLevel());
      game.state.start('EditorWorld', true, false, this._state.lvlId);
    });
    this.add(button);
  }

  /**
   * make navigation and export control group
   * @param {number} x
   * @param {number} y
   */
  _makeNavigationAndExportControls(x, y) {
    const state = this._state;
    const { levelDataMgr } = G.Helpers;

    // prev button
    this._makeTextFieldBtn(x + 200, y, 'Prev', () => {
      levelDataMgr.saveLevel(state.lvlId, this._exportLevel());
      game.state.start('Editor', true, false, levelDataMgr.getPreviousLevelId(state.lvlId));
    });
    // play button
    this._makeTextFieldBtn(x, y, 'Play', () => {
      levelDataMgr.saveLevel(state.lvlId, this._exportLevel());
      const levelData = { lvlIndex: state.lvlIndex, debugMode: true };
      G.lvlData = levelData;
      game.state.start('Game', true, false, levelData);
    });
    // next button
    this._makeTextFieldBtn(x + 350, y, 'Next', () => {
      levelDataMgr.saveLevel(state.lvlId, this._exportLevel());
      game.state.start('Editor', true, false, levelDataMgr.getNextLevelId(state.lvlId));
    });
    // export button
    this._makeTextFieldBtn(x + 560, y, 'EXPORT ALL CHANGES', () => {
      levelDataMgr.saveLevel(state.lvlId, this._exportLevel());
      levelDataMgr.exportLevels(this._storageManager.originalData);
    });
  }

  /**
   * make the undo and redo controls
   * @param {number} x
   * @param {number} y
   */
  _makeUndoAndRedoControls(x, y) {
    // undo button
    this._undoButton = this._makeTextFieldBtn(x, y, 'UNDO', () => {
      this.signals.undo.dispatch();
    });
    // redo button
    this._redoButton = this._makeTextFieldBtn(x + 335, y, 'REDO', () => {
      this.signals.redo.dispatch();
    });
  }

  /**
   * update the state of the undo and redo button
   * @param {boolean} undosAvailable
   * @param {boolean} redosAvailable
   * @param {string} undoStatusString
   */
  updateUndoAndRedoState(undosAvailable, redosAvailable, undoStatusString) {
    this._undoButton.alpha = undosAvailable ? 1 : 0.5;
    this._redoButton.alpha = redosAvailable ? 1 : 0.5;
    this._undoButton.text = `UNDO(${undoStatusString})`;
  }

  /**
   * make the reset level controls
   * @param {number} x
   * @param {number} y
   */
  _makeResetLevelControls(x, y) {
    this._resetButton = this._makeTextFieldBtn(x, y, 'RESET LEVEL CHANGES', async () => {
      const resetConfirmed = await G.Utils.confirmPrompt('Are you sure you would like to reset your changes to this level?');
      if (resetConfirmed) {
        this.signals.reset.dispatch();
      }
    });
  }

  /**
   * make the save status display
   * @param {number} x
   * @param {number} y
   */
  _makeSaveStatusDisplay(x, y) {
    this._saveSatus = this._makeTextField(x, y, '');
    this.updateSavedStatus();
  }

  /**
   * update level saved status
   */
  updateSavedStatus() {
    const hasChanges = !this._storageManager.wasLevelModified(this._lvlData);
    if (hasChanges) {
      const success = this._storageManager.lastSaveSuccessfull;
      this._saveSatus.text = success ? 'AUTO-SAVED ✔️' : 'AUTO-SAVED ❌';
      this._saveSatus.alpha = 1;
    } else {
      this._saveSatus.text = 'NO CHANGES';
      this._saveSatus.alpha = 0.5;
    }
  }

  /**
   * export level with changes
   */
  _exportLevel() {
    const lvlData = this._lvlData;
    const state = this._state;
    const tempArray = new G.GridArray(state.board.boardData.width, state.board.boardData.height);
    // loop through board and copy the board state to tempArray
    tempArray.loop((elem, x, y, data) => {
      data[x][y] = [];

      if (state.board.boardData.data[x][y] === 'X') data[x][y].push('X');

      const dirt = state.board.boardDirt.grid.data[x][y];
      if (dirt) data[x][y].push(`dirt${dirt.hp}`);

      const dirtS = state.board.boardDirtS.grid.data[x][y];
      if (dirtS) data[x][y].push(`dS${dirtS.hp}`);

      const jam = state.board.boardJam.grid.data[x][y];
      if (jam) data[x][y].push(`jam${jam.hp}`);

      const ice = state.board.boardIce.grid.data[x][y];
      if (ice) data[x][y].push(`ice${ice.hp}`);

      const cage = state.board.boardCage.grid.data[x][y];
      if (cage) data[x][y].push(`cn${cage.hp}`);

      const candy = state.board.boardCandies.grid.data[x][y];
      if (candy) data[x][y].push(candy.export());
    });

    // export level changes
    lvlData.predefinedDrops = state.dropZones.export();
    lvlData.levelData = tempArray.data;
    lvlData.collectCells = state.board.boardCollectCells.editorExport();
    return lvlData;
  }
}
